import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './en.json';
import ru from './ru.json';

/**
 * The console in Russian and in English.
 *
 * The chosen language is not only what the interface is drawn in: it is also
 * what every GraphQL request carries in `Accept-Language`, so the API's own
 * messages — errors, above all — come back in the same language as the labels
 * around them. `src/apollo/client.ts` reads `currentLocale()` per request for
 * exactly that reason.
 */

/** Where the detector caches the choice; the token uses the same prefix. */
export const LOCALE_STORAGE_KEY = 'talumi.admin.locale';

export const SUPPORTED_LOCALES = ['ru', 'en'] as const;

export type UiLocale = (typeof SUPPORTED_LOCALES)[number];

/** What an unknown or missing browser language falls back to. */
export const DEFAULT_LOCALE: UiLocale = 'ru';

// Resources are bundled rather than fetched: two small dictionaries are cheaper
// to ship than a loading state on every first paint, and it keeps `init`
// synchronous, which is what lets a test render without awaiting anything.
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      en: { translation: en },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    // `en-US` is English: match on the language subtag rather than refusing a
    // regional tag the browser happens to report.
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    // React escapes what it renders; doing it again turns « into &laquo;.
    interpolation: { escapeValue: false },
  });

/** The current language, narrowed to one the console actually has. */
export function currentLocale(): UiLocale {
  const language = i18n.resolvedLanguage ?? i18n.language;

  return isUiLocale(language) ? language : DEFAULT_LOCALE;
}

export function isUiLocale(value: string | undefined): value is UiLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export default i18n;
